import { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Download, Users, BarChart3, UserCheck, Church, Eye } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";

const COLORS = ["#1E90FF", "#38BDF8", "#2563EB", "#7C3AED", "#A78BFA", "#60A5FA", "#94A3B8"];
const TOOLTIP_STYLE = { background: "#0A0D14", border: "1px solid rgba(30,144,255,0.3)", borderRadius: 8 };

const ALL_COLUMNS = [
  { k: "name", label: "Nome" },
  { k: "age", label: "Idade" },
  { k: "bucket", label: "Faixa etária" },
  { k: "church", label: "Igreja" },
  { k: "contact", label: "Contato" },
  { k: "first_visit", label: "1ª visita" },
  { k: "attendances", label: "Presenças" },
];

export default function AdminReports() {
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ event_id: "", church: "", date_from: "", date_to: "" });
  const [columns, setColumns] = useState(["name", "age", "church", "contact", "attendances"]);
  const [previewOpen, setPreviewOpen] = useState(false);

  const buildStatsQS = () => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p.toString();
  };

  const load = () => api.get(`/youth-stats?${buildStatsQS()}`).then((r) => setStats(r.data));
  useEffect(() => { api.get("/events").then((r) => setEvents(r.data)); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const churches = useMemo(() => (stats?.church_distribution || []).map((c) => c.church).filter((c) => c !== "Não informado"), [stats]);

  const downloadPdf = async (type) => {
    try {
      const token = localStorage.getItem("ci_token");
      const p = new URLSearchParams();
      if (filters.event_id) p.set("event_id", filters.event_id);
      if (filters.church) p.set("church", filters.church);
      if (filters.date_from) p.set("date_from", filters.date_from);
      if (filters.date_to) p.set("date_to", filters.date_to);
      if (type === "nominal") p.set("columns", columns.join(","));
      const url = `${API}/reports/${type}.pdf?${p.toString()}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("Falha ao gerar PDF");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = type === "nominal" ? "lista_nominal.pdf" : "relatorio_estatistico.pdf";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("PDF gerado");
    } catch (e) { toast.error("Erro ao gerar PDF"); }
  };

  const toggleCol = (k) => setColumns((c) => c.includes(k) ? c.filter((x) => x !== k) : [...c, k]);

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <div className="label">Gestão de Jovens</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black mt-2">Relatórios</h1>
        <p className="text-sm text-slate-400 mt-1">Dashboard e exportação de PDFs com a identidade visual oficial.</p>
      </div>

      <div className="ci-card p-5 space-y-3">
        <div className="label">Filtros</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-slate-400 block mb-1">Encontro</label>
            <select value={filters.event_id} onChange={(e)=>setFilters({...filters, event_id: e.target.value})} className="ci-input text-sm">
              <option value="">Todos</option>
              {events.map((e) => <option key={e.id} value={e.id}>{(e.start_date || "").slice(0,10)} · {e.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Igreja</label>
            <select value={filters.church} onChange={(e)=>setFilters({...filters, church: e.target.value})} className="ci-input text-sm">
              <option value="">Todas</option>
              {churches.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">De</label>
            <input type="date" value={filters.date_from} onChange={(e)=>setFilters({...filters, date_from: e.target.value})} className="ci-input text-sm" />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Até</label>
            <input type="date" value={filters.date_to} onChange={(e)=>setFilters({...filters, date_to: e.target.value})} className="ci-input text-sm" />
          </div>
        </div>
      </div>

      {stats && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { l: "Jovens", v: stats.total_youth, i: Users },
              { l: "Completos", v: stats.complete, i: UserCheck },
              { l: "1ª vez", v: stats.first_timers, i: UserCheck },
              { l: "Retornaram", v: stats.returning, i: BarChart3 },
            ].map(({ l, v, i: Icon }) => (
              <div key={l} className="ci-card p-4">
                <Icon size={18} className="text-blue-400" />
                <div className="font-display text-3xl font-black mt-2 tabular-nums">{v}</div>
                <div className="text-xs text-slate-400 mt-1">{l}</div>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <div className="ci-card p-5">
              <div className="label mb-3">Faixa etária</div>
              <div className="h-56">
                <ResponsiveContainer>
                  <BarChart data={stats.age_distribution}>
                    <XAxis dataKey="bucket" stroke="#64748B" fontSize={10} interval={0} angle={-15} textAnchor="end" height={60}/>
                    <YAxis stroke="#64748B" fontSize={10} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                    <Bar dataKey="count" fill="#1E90FF" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="ci-card p-5">
              <div className="label mb-3">Por igreja (top)</div>
              <div className="h-56">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={stats.church_distribution.slice(0,6)} dataKey="count" nameKey="church" outerRadius={80} label={(e)=>e.church?.slice(0,12)}>
                      {stats.church_distribution.slice(0,6).map((d) => <Cell key={d.church} fill={COLORS[stats.church_distribution.indexOf(d) % COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {stats.attendance_by_event.length > 0 && (
            <div className="ci-card p-5">
              <div className="label mb-3">Evolução da presença</div>
              <div className="h-56">
                <ResponsiveContainer>
                  <BarChart data={stats.attendance_by_event}>
                    <XAxis dataKey="event_date" tickFormatter={(v) => (v || "").slice(5, 10)} stroke="#64748B" fontSize={10}/>
                    <YAxis stroke="#64748B" fontSize={10}/>
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                    <Bar dataKey="count" fill="#38BDF8" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </>
      )}

      {/* PDF A */}
      <div className="ci-card p-6 space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-blue-500/15 border border-blue-500/40 flex items-center justify-center shrink-0">
            <FileText size={22} className="text-blue-400"/>
          </div>
          <div className="flex-1">
            <div className="label">Relatório A</div>
            <h3 className="font-display text-xl font-bold mt-1">Estatístico (para o pastor)</h3>
            <p className="text-sm text-slate-400 mt-1">Dados agregados, gráficos, totais. Sem exposição de informações pessoais individuais.</p>
          </div>
        </div>
        <button data-testid="report-statistical-btn" onClick={() => downloadPdf("statistical")} className="btn-primary w-full sm:w-auto"><Download size={16}/> Gerar PDF Estatístico</button>
      </div>

      {/* PDF B */}
      <div className="ci-card p-6 space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-blue-500/15 border border-blue-500/40 flex items-center justify-center shrink-0">
            <FileText size={22} className="text-blue-400"/>
          </div>
          <div className="flex-1">
            <div className="label">Relatório B</div>
            <h3 className="font-display text-xl font-bold mt-1">Lista Nominal (uso interno)</h3>
            <p className="text-sm text-slate-400 mt-1">Lista detalhada de jovens. Restrito ao uso administrativo autorizado.</p>
          </div>
        </div>
        <div>
          <div className="text-xs text-slate-400 mb-2">Colunas incluídas:</div>
          <div className="flex gap-2 flex-wrap">
            {ALL_COLUMNS.map((c) => (
              <button key={c.k} type="button" onClick={() => toggleCol(c.k)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-all ${columns.includes(c.k) ? "bg-blue-500/15 border-blue-500/40 text-blue-300" : "bg-black/40 border-white/10 text-slate-400"}`}>
                {c.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setPreviewOpen(!previewOpen)} className="btn-ghost text-sm"><Eye size={14}/> {previewOpen ? "Ocultar" : "Prévia dos campos"}</button>
          <button data-testid="report-nominal-btn" onClick={() => downloadPdf("nominal")} className="btn-primary"><Download size={16}/> Gerar PDF Nominal</button>
        </div>
        {previewOpen && (
          <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-amber-200">
            Serão incluídos: <strong>{columns.map((k) => ALL_COLUMNS.find((c)=>c.k===k)?.label).join(", ")}</strong>. Esse PDF contém dados pessoais — manuseie com responsabilidade.
          </div>
        )}
      </div>
    </div>
  );
}
